import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchDepartmentOptions } from "#/features/employees/api";
import { getUserOptions } from "#/features/workflows/api";
import { lpoApi, ncrApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";

// NCR's raisedByUserId/authorizedByUserId are system user IDs, not employee IDs — sourced
// from GET /api/hr/users/options (gated by HR_EMPLOYEE_VIEW/HR_EMPLOYEE_MANAGE/WORKFLOW_VIEW/
// SUPER_ADMIN), confirmed by the backend team 2026-07-08.
function useUserPickerOptions() {
  const { data, loading, error } = useAsyncData(() => getUserOptions(), []);
  const options = (data ?? []).map((u) => ({ userId: u.id, label: u.fullName ? `${u.fullName} (${u.username ?? ""})` : (u.username ?? u.name ?? `User #${u.id}`) }));
  return { options, loading, error };
}

export default function NCRCreatePage() {
  const navigate = useNavigate();

  const { data: depts, loading: deptsLoading } = useAsyncData(() => fetchDepartmentOptions(), []);
  const { data: lpos, loading: lposLoading } = useAsyncData(
    () => lpoApi.list({ size: 200 }).then((r) => r.content),
    [],
  );
  const { options: userOptions, loading: usersLoading } = useUserPickerOptions();

  const [lpoId, setLpoId] = useState<number | "">("");
  const [deliveryNoteNo, setDeliveryNoteNo] = useState("");
  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [supplierName, setSupplierName] = useState("");
  const [description, setDescription] = useState("");
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [raisedByUserId, setRaisedByUserId] = useState<number | "">("");
  const [authorizedByUserId, setAuthorizedByUserId] = useState<number | "">("");

  const { mutate, loading, error } = useMutation(async () => {
    if (!lpoId) throw new Error("LPO is required.");
    if (!deliveryNoteNo.trim()) throw new Error("Delivery note number is required.");
    if (!departmentId) throw new Error("Department is required.");
    if (!supplierName.trim()) throw new Error("Supplier name is required.");
    if (!description.trim()) throw new Error("Description is required.");
    if (!correctiveAction.trim()) throw new Error("Corrective action is required.");
    const result = await ncrApi.create({
      lpoId: lpoId as number,
      deliveryNoteNo,
      departmentId: departmentId as number,
      supplierName,
      description,
      correctiveAction,
      raisedByUserId: raisedByUserId !== "" ? Number(raisedByUserId) : null,
      authorizedByUserId: authorizedByUserId !== "" ? Number(authorizedByUserId) : null,
    });
    navigate(`/procurement/ncrs/${result.id}`);
  });

  const loadingOptions = deptsLoading || lposLoading || usersLoading;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/ncrs" className="text-sm text-emerald-700 hover:underline">&larr; Back to NCRs</Link>
      </div>

      <ProcForm formNo="PROC-FRM-06" title="Non-Conformance Report">
        {loadingOptions && <ProcLoading label="Loading form options..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="NCR Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">LPO *</label>
              <select value={lpoId} onChange={(e) => setLpoId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" required>
                <option value="">Select LPO...</option>
                {(lpos ?? []).map((l) => <option key={l.id} value={l.id}>{l.serialNo} — {l.supplierName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Delivery Note No *</label>
              <input type="text" value={deliveryNoteNo} onChange={(e) => setDeliveryNoteNo(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Department *</label>
              <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" required>
                <option value="">Select department...</option>
                {(depts ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier Name *</label>
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Raised By</label>
              <select value={raisedByUserId} onChange={(e) => setRaisedByUserId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="">— select —</option>
                {userOptions.map((u) => <option key={u.userId} value={u.userId}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Authorized By</label>
              <select value={authorizedByUserId} onChange={(e) => setAuthorizedByUserId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="">— select —</option>
                {userOptions.map((u) => <option key={u.userId} value={u.userId}>{u.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Description *</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Describe the non-conformity in detail" className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Corrective Action *</label>
              <textarea value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value)} rows={4} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/ncrs" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save NCR"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
